import { useEffect, useMemo, useState } from 'react';
import { X, DollarSign, CreditCard, Send, Check, Plus, Trash2, Coins } from 'lucide-react';
import type { DivisionCuentaVista, PedidoDetalle } from '@shared/types';
import { Combine } from 'lucide-react';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirDocumento } from '@renderer/lib/impresionNavegador';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';

type MetodoPago = 'efectivo' | 'tarjeta' | 'transferencia';

interface PagoRow {
  metodo: MetodoPago;
  monto: string;
  referencia: string;
}

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  onClose: () => void;
  onCobrado: (pdfPath?: string) => void;
}

const filaInicial = (monto: number): PagoRow => ({
  metodo: 'efectivo',
  monto: monto > 0 ? monto.toFixed(2) : '',
  referencia: '',
});

function limpiarMonto(input: string): string {
  if (!input) return '';
  let limpio = input.replace(/[^0-9.]/g, '');
  const idx = limpio.indexOf('.');
  if (idx !== -1) {
    limpio = limpio.slice(0, idx + 1) + limpio.slice(idx + 1).replace(/\./g, '');
  }
  return limpio;
}

function parseMonto(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function CobroModal({ abierto, pedidoId, onClose, onCobrado }: Props) {
  const [detalle, setDetalle] = useState<PedidoDetalle | null>(null);
  const [pagos, setPagos] = useState<PagoRow[]>([filaInicial(0)]);
  const [propina, setPropina] = useState<string>('0');
  // Sprint 12.1a — modo simple: solo en simple usamos este input para el cálculo
  // directo de cambio. En modo mixto, el cajero captura monto por línea Y el
  // input "monto recibido en efectivo" se materializa abajo.
  const [montoRecibido, setMontoRecibido] = useState<string>('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  /**
   * Sprint 68 — ¿Esta cuenta está dividida?
   *
   * Reporte del negocio: *"al momento de pagar en ningún lugar indica que la cuenta fue dividida
   * y que hay que estar pendiente que se paguen todas las partes"*. Es fuga de dinero: se cobra
   * una parte, el modal dice "cobrado", y las otras se van.
   */
  const [division, setDivision] = useState<DivisionCuentaVista | null>(null);
  const negocio = useNegocioStore((s) => s.negocio);

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  useEffect(() => {
    if (!abierto || !pedidoId) {
      setDetalle(null);
      setPagos([filaInicial(0)]);
      setPropina('0');
      setMontoRecibido('');
      setError(null);
      setDivision(null);
      return;
    }
    setCargando(true);
    // Se consulta en paralelo al detalle: es una lectura chica y no debe retrasar el cobro.
    void window.api.pedidos.division(pedidoId).then((res) => {
      setDivision(res.ok ? res.data : null);
    });
    window.api.pedidos.obtener(pedidoId).then((res) => {
      if (res.ok && res.data) {
        setDetalle(res.data);
        // Sprint 39 — `detalle.*` viene en centavos; los strings de input
        // viven en pesos para conservar la UX previa (decimales libres).
        /**
         * Sprint 69 (H-9) — El envío contra-entrega se restaba DOS veces.
         *
         * `Pedido.total` ya excluye el envío cuando lo cobra el repartidor (`pedidoService` no lo
         * suma al total en ese caso), así que restarlo otra vez aquí proponía cobrar $170 de un
         * pedido de $200 — y como el backend exige que la suma de pagos cuadre exacto con el
         * total, el cobro **fallaba** hasta que el operador corrigiera el monto a mano. Con
         * "cliente paga al recibir" como opción por default, eso rompía el cobro de todo domicilio
         * con cuota de envío.
         */
        const cobrableCentavos = res.data.total;
        setPagos([filaInicial(centavosAPesos(cobrableCentavos))]);
        setPropina('0');
        setMontoRecibido(''); // el cajero teclea con cuánto le pagan
      }
      setCargando(false);
    });
  }, [abierto, pedidoId]);

  // Sprint 39 — backend devuelve centavos; aquí trabajamos en pesos para
  // mantener la aritmética interna (que mezcla con strings de input vía
  // `parseMonto`). Al hablar con backend/formatPrecio reconvertimos.
  const total = detalle ? centavosAPesos(detalle.total) : 0;
  const subtotalAlimentos = detalle ? centavosAPesos(detalle.subtotal + detalle.impuesto) : 0;
  const costoEnvio = detalle ? centavosAPesos(detalle.costoEnvio) : 0;
  const envioContraEntrega = detalle?.envioPagaCliente ?? false;
  // Total del pedido SIN propina (subtotal venta).
  /**
   * Sprint 69 (H-9) — Lo que se cobra en caja es `Pedido.total`, punto.
   *
   * `total` ya excluye el envío cuando lo cobra el repartidor, así que la resta de aquí lo quitaba
   * por segunda vez: la pantalla proponía $170 de un pedido de $200, el porcentaje de propina se
   * calculaba sobre el número equivocado, y el backend rechazaba el cobro por no cuadrar.
   */
  const totalCobrable = total;

  const propinaNum = parseMonto(propina);
  // Sprint 12.1a fix B — total visible al cliente = pedido + propina.
  const totalConPropina = totalCobrable + propinaNum;

  // Sprint 12.1a fix A — modo simple: 1 línea Y efectivo.
  // En este modo, el cajero solo teclea "con cuánto paga" → cambio automático.
  const esModoSimple = pagos.length === 1 && pagos[0].metodo === 'efectivo';

  const sumaMontos = useMemo(
    () => pagos.reduce((acc, p) => acc + parseMonto(p.monto), 0),
    [pagos],
  );

  // Sprint 21 — el `monto` UI de cada pago representa el TOTAL cobrado por
  // ese método (incluye propina si es la primera línea). El backend
  // descompone al persistir: `pago.monto = montoUI - propinaAsignada`.
  // Esto elimina la inconsistencia donde el panel decía $533.60 pero el
  // cuadre comparaba contra $464.
  const diferencia = totalConPropina - sumaMontos;
  const cuadra = Math.abs(diferencia) < 0.01 && totalConPropina > 0;
  const falta = diferencia > 0.01;

  // --- Cálculo de efectivo necesario y cambio ---
  const hayEfectivo = pagos.some((p) => p.metodo === 'efectivo');
  const montoEfectivoEnPagos = pagos
    .filter((p) => p.metodo === 'efectivo')
    .reduce((a, p) => a + parseMonto(p.monto), 0);
  // Sprint 21 — el monto del pago efectivo YA incluye propina si es fila 0.
  // No la sumamos aparte.
  const efectivoNecesario = montoEfectivoEnPagos;

  // Sprint 21 — Sync auto: cuando hay 1 sola fila de pago, el monto[0] siempre
  // refleja el TOTAL a cobrar (incluye propina). Esto evita que la dueña vea
  // "$464 cuadra" mientras el panel arriba dice "$533.60" porque agregó 15%
  // de propina después de abrir el modal. La sincronización solo aplica con
  // 1 fila — en modo mixto la dueña edita montos manualmente.
  useEffect(() => {
    if (!detalle) return;
    if (pagos.length !== 1) return;
    const objetivo = totalConPropina.toFixed(2);
    if (pagos[0].monto !== objetivo) {
      setPagos((prev) => [{ ...prev[0], monto: objetivo }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propinaNum, totalCobrable, detalle, pagos.length]);

  // En modo simple, montoRecibido = lo que el cliente entrega.
  // En modo mixto, montoRecibido = lo que el cliente entrega en efectivo solamente.
  const recibidoNum = parseMonto(montoRecibido);
  const cambio = hayEfectivo ? Math.max(0, recibidoNum - efectivoNecesario) : 0;
  const efectivoInsuficiente = hayEfectivo && recibidoNum > 0 && recibidoNum + 0.001 < efectivoNecesario;
  /**
   * Sprint 112 — Dejar el campo vacío **es** decir "paga exacto".
   *
   * El comentario que estaba aquí ya decía lo correcto —*"si el cajero no ha tecleado nada o
   * teclea exacto, no hay falta"*— y la línea de abajo hacía lo contrario: con el campo vacío
   * bloqueaba el botón de cobrar. Y como el campo muestra el total como *placeholder* gris, en
   * pantalla se lee idéntico a un campo ya lleno: el operador ve "20.00", aprieta *Cobrar y emitir
   * ticket*, no pasa nada, y **nada le dice por qué**. Justo el caso más común del mostrador —el
   * cliente paga exacto— era el único que no se podía cobrar. Visto en producción el 2026-08-26.
   *
   * El servidor ya lo soportaba sin cambios: `montoRecibidoEfectivo` es opcional y, si no viaja,
   * el cambio queda en cero — que es exactamente lo que significa un pago exacto.
   *
   * Si el cliente entrega de más, se teclea igual que siempre y el cambio aparece en grande.
   */
  const pagaExacto = esModoSimple && montoRecibido.trim() === '';

  const algunPagoVacio = pagos.some((p) => parseMonto(p.monto) <= 0);

  if (!abierto) return null;

  const actualizarPago = (idx: number, parche: Partial<PagoRow>) => {
    const limpio: Partial<PagoRow> = { ...parche };
    if (parche.monto !== undefined) limpio.monto = limpiarMonto(parche.monto);
    setPagos((prev) => prev.map((p, i) => (i === idx ? { ...p, ...limpio } : p)));
  };

  const agregarPago = () => {
    const restante = Math.max(0, diferencia);
    setPagos((prev) => [
      ...prev,
      { metodo: 'tarjeta', monto: restante > 0 ? restante.toFixed(2) : '', referencia: '' },
    ]);
    // Al pasar a modo mixto, si no hay monto recibido capturado, lo prellenamos
    // con el efectivo necesario (para que el cajero solo ajuste si recibió más).
    if (montoRecibido === '') {
      setMontoRecibido(efectivoNecesario.toFixed(2));
    }
  };

  const eliminarPago = (idx: number) => {
    setPagos((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const aplicarPresetPropina = (porcentaje: number) => {
    setPropina((totalCobrable * porcentaje).toFixed(2));
  };

  const handleConfirmar = async () => {
    if (!pedidoId || !detalle) return;
    if (algunPagoVacio) {
      setError('Cada pago debe tener un monto mayor a 0.');
      return;
    }
    if (!cuadra) {
      const msg = falta
        ? `Faltan ${formatPrecio(pesosACentavos(diferencia), monedaSimbolo, locale)} por cubrir.`
        : `Sobran ${formatPrecio(pesosACentavos(-diferencia), monedaSimbolo, locale)} en los pagos. Ajusta los montos.`;
      setError(msg);
      return;
    }
    if (efectivoInsuficiente) {
      setError('El monto recibido en efectivo es insuficiente para cubrir el total + propina.');
      return;
    }

    // Sprint 21 — validar que el primer pago cubra la propina (porque la
    // descompondremos de ahí al persistir).
    if (propinaNum > 0 && pagos.length > 0 && parseMonto(pagos[0].monto) < propinaNum - 0.001) {
      setError(
        `El primer pago (${formatPrecio(pesosACentavos(parseMonto(pagos[0].monto)), monedaSimbolo, locale)}) ` +
        `debe ser de al menos ${formatPrecio(pesosACentavos(propinaNum), monedaSimbolo, locale)} para incluir la propina.`,
      );
      return;
    }

    setEnviando(true);
    setError(null);

    // Sprint 21 — el `monto` UI incluye propina cuando es la primera fila.
    // El backend espera `pago.monto = subtotal aportado` y `pago.propina`
    // por separado, así que descomponemos aquí.
    // Sprint 39 — convertir pesos UI → centavos antes de enviar al backend.
    const pagosPayload = pagos.map((p, i) => {
      const montoTotal = parseMonto(p.monto);
      const propinaPago = i === 0 ? propinaNum : 0;
      return {
        metodo: p.metodo,
        monto: pesosACentavos(Math.max(0, montoTotal - propinaPago)),
        propina: pesosACentavos(propinaPago),
        referencia: p.referencia.trim() || undefined,
      };
    });

    const res = await window.api.pedidos.cobrar({
      pedidoId,
      pagos: pagosPayload,
      montoRecibidoEfectivo: hayEfectivo && recibidoNum > 0 ? pesosACentavos(recibidoNum) : undefined,
    });

    if (!res.ok) {
      // Sprint 37 — Traducir códigos técnicos a frases naturales.
      setError(traducirErrorBackend(res.error, res.codigo));
      setEnviando(false);
      return;
    }

    // En desktop esto imprime en la térmica y deja el PDF histórico. En web
    // sólo deja el PDF: el servidor vive en el VPS y no ve la impresora.
    const ticketRes = await window.api.ticket.imprimir(pedidoId);

    // Sprint 56 — en navegador la impresión física la dispara el cliente.
    // El cobro ya está hecho: si la impresión falla no se revierte nada ni se
    // muestra error de cobro; el ticket queda como PDF y se puede reimprimir.
    if (esWeb()) {
      const pref = await window.api.config.obtener('web_auto_imprimir_ticket');
      const autoImprimir = !pref.ok || pref.data !== 'false';
      if (autoImprimir) {
        /**
         * Sprint 156 — El resultado de imprimir ya no se tira a la basura.
         *
         * Se descartaba entero: si el ticket no salía por ningún lado, el cobro
         * seguía su camino y la cajera se enteraba mirando la impresora. Sólo se
         * avisa cuando fallaron **los dos** caminos —la impresora de la caja y la
         * ventana del navegador—, porque un aviso en cada venta sería ruido y
         * acabaría ignorándose. Que la impresora esté apagada ya lo dice la barra
         * de abajo; esto es para cuando no salió nada en absoluto.
         */
        const impresion = await imprimirDocumento({ tipo: 'caja', pedidoId });
        if (!impresion.ok) {
          window.alertar({
            titulo: 'El ticket no se imprimió',
            mensaje:
              impresion.motivo === 'sesion_vencida'
                ? 'Tu sesión se cerró. Vuelve a entrar y reimprime el ticket desde el pedido; ' +
                  'el cobro ya quedó guardado.'
                : 'El cobro quedó guardado. Puedes reimprimir el ticket desde el pedido cuando ' +
                  'la impresora esté lista.',
            variant: 'warn',
          });
        }
      }
    }

    /**
     * Sprint 68 — El recordatorio, en el momento en que sirve.
     *
     * Aquí es donde se pierde el dinero: el cobro salió bien, el ticket se imprimió, y si nadie
     * dice nada, quien cobró da la mesa por liquidada. Se avisa con un modal bloqueante —no un
     * toast que se va solo— porque es la última oportunidad antes de que el cliente se levante.
     *
     * Se relee el estado en lugar de usar el que se cargó al abrir: entre que se abrió el modal y
     * se cobró, otro cajero pudo haber cobrado otra parte.
     */
    const divPost = await window.api.pedidos.division(pedidoId);
    if (divPost.ok && divPost.data && divPost.data.faltanPorCobrar > 0) {
      const d = divPost.data;
      const faltantes = d.partes.filter((p) => !p.pagado);
      await window.alertar({
        titulo: `Falta cobrar ${faltantes.length} de ${d.totalPartes} partes`,
        mensaje:
          `Esta cuenta está dividida. Todavía no cobras:\n\n` +
          faltantes
            .map(
              (p) =>
                `• ${p.etiqueta ?? `Parte #${String(p.numeroPedido).padStart(5, '0')}`} — ` +
                `${formatPrecio(p.total, monedaSimbolo, locale)}`,
            )
            .join('\n') +
          `\n\nTotal pendiente: ${formatPrecio(d.faltanPorCobrar, monedaSimbolo, locale)}. ` +
          `No liberes la mesa todavía.`,
        variant: 'warn',
      });
    }

    setEnviando(false);
    if (ticketRes.ok) {
      onCobrado(ticketRes.data.pdfPath);
    } else {
      setError(`Cobrado pero no se pudo generar ticket: ${ticketRes.error}`);
      onCobrado(undefined);
    }
  };

  // Botón "Cobrar" deshabilitado si: no cuadra, faltan datos, etc.
  const puedeConfirmar =
    !!detalle &&
    !enviando &&
    cuadra &&
    !algunPagoVacio &&
    !efectivoInsuficiente;
  // Sprint 112 — El campo de "con cuánto paga" vacío ya no bloquea: significa que paga exacto.

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">Cobrar pedido</h2>
            {detalle && (
              <p className="text-xs text-slate-500">
                #{String(detalle.numeroPedido).padStart(5, '0')} ·{' '}
                {/*
                  Sprint 112 — Tres formas de vender, no dos.

                  Esto era binario (`comer_aqui` o si no, "A domicilio") desde antes de que
                  existiera el mostrador, así que **toda venta de mostrador se anunciaba como
                  "Comer aquí"** justo en la pantalla donde se cobra. Visto en producción el
                  2026-08-26 sobre la venta #00007, que era para llevar.

                  `paraRecoger` manda sobre `tipo`: un pedido de mostrador viaja como
                  `comer_aqui` en el campo viejo, y lo que lo distingue es que el cliente se lo
                  lleva. Mismo criterio que usa el carrito desde el Sprint 111.
                */}
                {detalle.paraRecoger
                  ? 'Venta de mostrador'
                  : detalle.tipo === 'comer_aqui'
                    ? 'Comer aquí'
                    : 'A domicilio'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {/*
            Sprint 68 — Que se vea ANTES de teclear el efectivo: esto es una parte, no la cuenta.
            Sin esto, el cajero cobra $240 de una cuenta de $960 y nada se lo dice.
          */}
          {division && (
            <div className="mx-5 mt-3 rounded-lg border-2 border-indigo-300 bg-indigo-50 px-3 py-2">
              <div className="flex items-center gap-2 text-sm font-bold text-indigo-900">
                <Combine className="h-4 w-4" />
                Cuenta dividida · parte {division.posicionDeEste} de {division.totalPartes}
              </div>
              {/*
              `faltanPorCobrar` incluye ESTA parte, que todavía no se ha cobrado. Lo que importa
              decirle al cajero es lo que va a seguir faltando DESPUÉS de cobrar la que tiene
              enfrente — si no, la rama de "es la última" nunca se mostraría.
            */}
            {division.partes.filter((p) => !p.pagado && !p.esEste).length > 0 ? (
                <p className="mt-0.5 text-xs text-indigo-800">
                  Después de esta parte seguirán faltando{' '}
                  <strong>
                    {formatPrecio(division.faltanPorCobrar - (detalle?.total ?? 0), monedaSimbolo, locale)}
                  </strong>{' '}
                  en {division.partes.filter((p) => !p.pagado && !p.esEste).length} parte(s). No
                  liberes la mesa hasta cobrarlas todas.
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-indigo-800">
                  Es la última parte que falta: con ésta se liquida la cuenta completa.
                </p>
              )}
            </div>
          )}

          {cargando ? (
            <div className="text-center text-slate-400 py-8">Cargando...</div>
          ) : !detalle ? (
            <div className="text-center text-slate-400 py-8">Pedido no disponible.</div>
          ) : (
            <>
              {/* === PANEL TOTAL — Sprint 12.1a fix B: muestra subtotal + propina = total === */}
              <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
                {detalle.tipo === 'delivery' && costoEnvio > 0 && (
                  <div className="text-xs text-slate-600 space-y-0.5 border-b border-primary/20 pb-2 mb-2">
                    <div className="flex justify-between">
                      <span>Subtotal alimentos</span>
                      <span className="font-mono">{formatPrecio(pesosACentavos(subtotalAlimentos), monedaSimbolo, locale)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Envío {envioContraEntrega ? '(paga al recibir)' : '(incluido)'}</span>
                      <span className="font-mono">{formatPrecio(pesosACentavos(costoEnvio), monedaSimbolo, locale)}</span>
                    </div>
                  </div>
                )}
                {propinaNum > 0 ? (
                  // Con propina: mostrar desglose Subtotal / +Propina / =TOTAL
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Subtotal pedido</span>
                      <span className="font-mono">{formatPrecio(pesosACentavos(totalCobrable), monedaSimbolo, locale)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-yellow-700">
                      <span>+ Propina</span>
                      <span className="font-mono">{formatPrecio(pesosACentavos(propinaNum), monedaSimbolo, locale)}</span>
                    </div>
                    <div className="border-t border-primary/30 pt-1 mt-1 flex justify-between items-baseline">
                      <span className="text-sm text-slate-600">TOTAL a cobrar</span>
                      <span className="text-2xl font-bold text-primary font-mono">
                        {formatPrecio(pesosACentavos(totalConPropina), monedaSimbolo, locale)}
                      </span>
                    </div>
                  </div>
                ) : (
                  // Sin propina: layout limpio centrado como antes
                  <div className="text-center">
                    <div className="text-sm text-slate-500">
                      {envioContraEntrega ? 'Cobrar ahora' : 'Total a cobrar'}
                    </div>
                    <div className="text-3xl font-bold text-primary font-mono">
                      {formatPrecio(pesosACentavos(totalCobrable), monedaSimbolo, locale)}
                    </div>
                  </div>
                )}
                {envioContraEntrega && costoEnvio > 0 && (
                  <div className="text-xs text-amber-700 mt-2 text-center">
                    ⚠ Repartidor cobrará {formatPrecio(pesosACentavos(costoEnvio), monedaSimbolo, locale)} adicionales al cliente.
                  </div>
                )}
              </div>

              {/* === MODO SIMPLE (solo efectivo, 1 línea) — Sprint 12.1a fix A === */}
              {esModoSimple ? (
                <ModoSimpleEfectivo
                  totalConPropina={totalConPropina}
                  monedaSimbolo={monedaSimbolo}
                  locale={locale}
                  montoRecibido={montoRecibido}
                  setMontoRecibido={(v) => setMontoRecibido(limpiarMonto(v))}
                  cambio={cambio}
                  pagaExacto={pagaExacto}
                  efectivoInsuficiente={efectivoInsuficiente}
                  onCambiarMetodo={(m) =>
                    actualizarPago(0, {
                      metodo: m,
                      // Al pasar a tarjeta/transfer, el monto del pago debe igualar el subtotal cobrable.
                      monto: totalCobrable.toFixed(2),
                    })
                  }
                  onAgregarMetodo={agregarPago}
                  enviando={enviando}
                />
              ) : (
                <>
                  {/* === MODO MIXTO — múltiples líneas o método no-efectivo === */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label !mb-0">Pagos ({pagos.length})</div>
                      <button
                        type="button"
                        onClick={agregarPago}
                        className="btn-ghost text-xs flex items-center gap-1"
                        disabled={enviando}
                      >
                        <Plus className="h-3 w-3" /> Agregar pago
                      </button>
                    </div>
                    <div className="space-y-2">
                      {pagos.map((p, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <BotonMetodoMini
                              activo={p.metodo === 'efectivo'}
                              onClick={() => actualizarPago(i, { metodo: 'efectivo' })}
                              icon={DollarSign}
                              label="Efectivo"
                            />
                            <BotonMetodoMini
                              activo={p.metodo === 'tarjeta'}
                              onClick={() => actualizarPago(i, { metodo: 'tarjeta' })}
                              icon={CreditCard}
                              label="Tarjeta"
                            />
                            <BotonMetodoMini
                              activo={p.metodo === 'transferencia'}
                              onClick={() => actualizarPago(i, { metodo: 'transferencia' })}
                              icon={Send}
                              label="Transfer."
                            />
                            {pagos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => eliminarPago(i)}
                                className="btn-ghost p-2 text-red-600"
                                aria-label="Eliminar pago"
                                disabled={enviando}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              className="input flex-1 font-mono text-right"
                              placeholder="Monto"
                              value={p.monto}
                              onChange={(e) => actualizarPago(i, { monto: e.target.value })}
                              disabled={enviando}
                            />
                            {/* Sprint 12.1a fix C — Etiqueta/Cliente para los 3 métodos. */}
                            <input
                              type="text"
                              className="input flex-1 text-xs"
                              placeholder="Cliente o referencia (opc)"
                              value={p.referencia}
                              onChange={(e) => actualizarPago(i, { referencia: e.target.value })}
                              disabled={enviando}
                              maxLength={50}
                            />
                          </div>
                          {/* Sprint 21 — aviso claro: la propina se cobra en la primera fila. */}
                          {i === 0 && propinaNum > 0 && (
                            <div className="text-[11px] text-amber-700 italic flex items-center gap-1">
                              <Coins className="h-3 w-3" />
                              Incluye {formatPrecio(pesosACentavos(propinaNum), monedaSimbolo, locale)} de propina
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Indicador de cuadre (mixto) */}
                  <div
                    className={`rounded-lg px-4 py-3 text-sm border ${
                      cuadra
                        ? 'bg-green-50 text-green-800 border-green-200'
                        : falta
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : 'bg-rose-50 text-rose-800 border-rose-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {cuadra ? '✓ Cuadre exacto' : falta ? 'Falta cobrar' : 'Sobra en pagos'}
                      </span>
                      <span className="font-mono font-bold">
                        {cuadra
                          ? formatPrecio(0, monedaSimbolo, locale)
                          : formatPrecio(pesosACentavos(Math.abs(diferencia)), monedaSimbolo, locale)}
                      </span>
                    </div>
                    <div className="text-xs mt-1 opacity-75">
                      Suma pagos: {formatPrecio(pesosACentavos(sumaMontos), monedaSimbolo, locale)} · A cobrar:{' '}
                      {formatPrecio(pesosACentavos(totalConPropina), monedaSimbolo, locale)}
                      {propinaNum > 0 && ' (incluye propina)'}
                    </div>
                  </div>

                  {/* Monto recibido en efectivo (solo si hay efectivo entre las líneas) */}
                  {hayEfectivo && (
                    <div>
                      <label className="label" htmlFor="montoRMix">
                        Monto recibido en efectivo
                        <span className="text-xs text-slate-500 ml-2 font-normal">
                          (a cubrir: {formatPrecio(pesosACentavos(efectivoNecesario), monedaSimbolo, locale)})
                        </span>
                      </label>
                      <input
                        id="montoRMix"
                        type="text"
                        inputMode="decimal"
                        className={`input text-2xl font-mono font-bold text-right ${
                          efectivoInsuficiente ? 'input-error' : ''
                        }`}
                        value={montoRecibido}
                        onChange={(e) => setMontoRecibido(limpiarMonto(e.target.value))}
                        disabled={enviando}
                      />
                      <div
                        className={`mt-2 rounded-lg px-4 py-2 text-center ${
                          efectivoInsuficiente
                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                            : cambio > 0
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-slate-50 text-slate-500 border border-slate-200'
                        }`}
                      >
                        <div className="text-xs">
                          {efectivoInsuficiente
                            ? 'Falta efectivo'
                            : cambio > 0
                              ? 'Cambio a entregar'
                              : 'Sin cambio'}
                        </div>
                        <div className="text-xl font-mono font-bold">
                          {formatPrecio(
                            pesosACentavos(efectivoInsuficiente ? efectivoNecesario - recibidoNum : cambio),
                            monedaSimbolo,
                            locale,
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* === PROPINA — siempre visible (modo simple o mixto) === */}
              <div>
                <label className="label flex items-center gap-1" htmlFor="propinaInput">
                  <Coins className="h-4 w-4 text-yellow-600" /> Propina (opcional)
                </label>
                <input
                  id="propinaInput"
                  type="text"
                  inputMode="decimal"
                  className="input font-mono text-right"
                  value={propina}
                  onChange={(e) => setPropina(limpiarMonto(e.target.value))}
                  disabled={enviando}
                />
                <div className="mt-2 flex gap-1">
                  <button type="button" onClick={() => setPropina('0')} className="btn-ghost flex-1 text-xs">
                    Sin propina
                  </button>
                  <button type="button" onClick={() => aplicarPresetPropina(0.1)} className="btn-ghost flex-1 text-xs">
                    10%
                  </button>
                  <button type="button" onClick={() => aplicarPresetPropina(0.15)} className="btn-ghost flex-1 text-xs">
                    15%
                  </button>
                  <button type="button" onClick={() => aplicarPresetPropina(0.2)} className="btn-ghost flex-1 text-xs">
                    20%
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700 border border-red-200" role="alert">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-3 flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button onClick={handleConfirmar} disabled={!puedeConfirmar} className="btn-primary btn-lg flex-1">
            <Check className="h-5 w-5" />
            {enviando ? 'Cobrando...' : 'Cobrar y emitir ticket'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-componentes
// ============================================================================

function BotonMetodoMini({
  activo,
  onClick,
  icon: Icon,
  label,
}: {
  activo: boolean;
  onClick: () => void;
  icon: typeof DollarSign;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 flex-col items-center gap-0.5 rounded border px-2 py-1.5 transition text-xs ${
        activo
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="font-medium">{label}</span>
    </button>
  );
}

/**
 * Sprint 12.1a fix A — UI condensada para el caso más común: 1 línea, efectivo.
 * Fusiona "Pago" + "Monto recibido" en un único input: "¿Con cuánto paga?".
 * El cambio aparece grande y verde inmediatamente. Sin avisos confusos de
 * "EXCEDE EL TOTAL" — pagar más que el total es lo NORMAL en efectivo.
 */
function ModoSimpleEfectivo(props: {
  totalConPropina: number;
  monedaSimbolo: string;
  locale: string;
  montoRecibido: string;
  setMontoRecibido: (v: string) => void;
  cambio: number;
  /** Sprint 112 — Campo vacío = el cliente paga exacto. No es un error, es el caso común. */
  pagaExacto: boolean;
  efectivoInsuficiente: boolean;
  onCambiarMetodo: (m: MetodoPago) => void;
  onAgregarMetodo: () => void;
  enviando: boolean;
}) {
  const {
    totalConPropina,
    monedaSimbolo,
    locale,
    montoRecibido,
    setMontoRecibido,
    cambio,
    pagaExacto,
    efectivoInsuficiente,
    onCambiarMetodo,
    onAgregarMetodo,
    enviando,
  } = props;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
      {/* Selector de método (compacto) + botón para pasar a mixto */}
      <div className="flex items-center gap-2">
        <BotonMetodoMini activo={true} onClick={() => {}} icon={DollarSign} label="Efectivo" />
        <BotonMetodoMini activo={false} onClick={() => onCambiarMetodo('tarjeta')} icon={CreditCard} label="Tarjeta" />
        <BotonMetodoMini activo={false} onClick={() => onCambiarMetodo('transferencia')} icon={Send} label="Transfer." />
        <button
          type="button"
          onClick={onAgregarMetodo}
          className="btn-ghost text-xs flex items-center gap-1 whitespace-nowrap"
          disabled={enviando}
          title="Cobrar con más de un método"
        >
          <Plus className="h-3 w-3" /> Otro método
        </button>
      </div>

      <div>
        <label className="label" htmlFor="montoRecibidoSimple">
          ¿Con cuánto paga el cliente?
        </label>
        <input
          id="montoRecibidoSimple"
          type="text"
          inputMode="decimal"
          autoFocus
          className={`input text-3xl font-mono font-bold text-right ${
            efectivoInsuficiente ? 'input-error' : ''
          }`}
          placeholder={totalConPropina.toFixed(2)}
          value={montoRecibido}
          onChange={(e) => setMontoRecibido(e.target.value)}
          disabled={enviando}
        />
        <div className="mt-1 text-xs text-slate-500 text-right">
          Total a pagar:{' '}
          <span className="font-mono font-semibold text-slate-700">
            {formatPrecio(pesosACentavos(totalConPropina), monedaSimbolo, locale)}
          </span>
          {/*
            Sprint 112 — El tip decía "deja el campo en $X" y el campo estaba **vacío** (eso era
            el placeholder), así que "dejarlo así" bloqueaba el cobro. Ahora dice la verdad y
            describe el atajo real: no escribir nada.
          */}
          {' '}· Si paga exacto, no escribas nada
        </div>
      </div>

      {/* Indicador de cambio / falta / OK */}
      {pagaExacto ? (
        /*
          Sprint 112 — Vacío ya no es un regaño, es el caso común resuelto.

          Antes decía "Escribe con cuánto paga el cliente para ver el cambio" con el botón de
          cobrar apagado: una instrucción obligatoria disfrazada de sugerencia. Ahora el campo
          vacío se lee como lo que es —paga exacto— y se puede cobrar de inmediato.
        */
        <div className="rounded-lg bg-slate-50 px-4 py-2 text-center border border-slate-200">
          <div className="text-xs text-slate-600">✓ Pago exacto — sin cambio</div>
          <div className="text-xs text-slate-400">
            Si te dio más, escribe con cuánto pagó y te calculo el cambio.
          </div>
        </div>
      ) : efectivoInsuficiente ? (
        <div className="rounded-lg bg-rose-50 px-4 py-3 text-center border border-rose-200">
          <div className="text-xs text-rose-700">Falta efectivo</div>
          <div className="text-2xl font-mono font-bold text-rose-700">
            {formatPrecio(pesosACentavos(totalConPropina - parseMonto(montoRecibido)), monedaSimbolo, locale)}
          </div>
        </div>
      ) : cambio > 0 ? (
        <div className="rounded-lg bg-green-50 px-4 py-3 text-center border-2 border-green-200">
          <div className="text-xs text-green-700 font-medium">CAMBIO A ENTREGAR</div>
          <div className="text-3xl font-mono font-bold text-green-700">
            {formatPrecio(pesosACentavos(cambio), monedaSimbolo, locale)}
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-slate-50 px-4 py-2 text-center border border-slate-200">
          <div className="text-xs text-slate-600">✓ Pago exacto — sin cambio</div>
        </div>
      )}
    </div>
  );
}
